import { StyleSheet, View } from "react-native";

import { useAppTheme } from "@/theme/app-theme";

/**
 * The small on/off switch used beside a toggle's label.
 *
 * Not a `Switch` from react-native: this is never pressed on its own. The whole
 * row is one `Pressable` and this is the part of it that shows the state, so it
 * must not take touches or grow its own hit target — a real switch inside a
 * pressable row gives you two overlapping controls that disagree about what a
 * tap on the edge means.
 *
 * Lifted out of `home.tsx`, where it was a private component reading its
 * geometry out of that screen's stylesheet. The inbox's "I'm Open" control
 * needed the same switch, and the alternative was a second copy of the track
 * and thumb measurements in a second five-thousand-line screen — which is how
 * two controls that are meant to look identical stop looking identical.
 *
 * It owns its own styles for that reason, rather than taking a `styles` prop
 * as it did before: a shared control whose appearance depends on which screen
 * passed the stylesheet is not actually shared.
 */
export function CompactSwitch({
  enabled,
  disabled = false,
}: {
  enabled: boolean;
  /** Dims the switch. The row above it owns `disabled` for touches. */
  disabled?: boolean;
}) {
  const { colors } = useAppTheme();
  return (
    <View
      style={[
        styles.track,
        enabled && styles.trackEnabled,
        disabled && styles.disabled,
      ]}
      // The label beside it already carries the state to a screen reader, and
      // the row is the control. Announcing this as well reads the same fact
      // twice.
      accessibilityElementsHidden
      importantForAccessibility="no"
    >
      <View
        style={[
          styles.thumb,
          { shadowColor: colors.shadow },
          enabled && styles.thumbEnabled,
        ]}
      />
    </View>
  );
}

const styles = StyleSheet.create({
  track: {
    width: 38,
    height: 22,
    padding: 3,
    justifyContent: "center",
    borderRadius: 11,
    borderWidth: 1,
    borderColor: "rgba(255,255,255,0.08)",
    backgroundColor: "rgba(75, 96, 131, 0.42)",
  },

  trackEnabled: {
    borderColor: "rgba(109, 224, 166, 0.28)",
    backgroundColor: "#46B978",
  },

  disabled: {
    opacity: 0.6,
  },

  thumb: {
    width: 14,
    height: 14,
    borderRadius: 7,
    backgroundColor: "#DCE6F3",
    shadowOpacity: 0.16,
    shadowRadius: 3,
    shadowOffset: {
      width: 0,
      height: 1,
    },
    elevation: 2,
  },

  // Travel is the track's width less its padding, border and the thumb — the
  // thumb lands exactly on the far inset rather than near it.
  thumbEnabled: {
    transform: [{ translateX: 16 }],
  },
});
